/**
 * O "@" da nota interna (#569): onde ele está, quem ele busca, e como a
 * escolha entra no texto.
 *
 * ── Diferente do "/" das mensagens prontas ────────────────────────────────
 *
 * O "/" é o campo inteiro: ele só existe no começo, e escolher SUBSTITUI o
 * texto. O "@" vive no MEIO da frase ("pode aprovar, @Maria?"), então ele é
 * lido na altura do cursor, e escolher troca só o pedaço "@Ma" por
 * "@Maria Souza ", sem tocar no resto.
 *
 * O termo pode ter espaço ("@Maria So"): nomes têm sobrenome. O que encerra a
 * menção é a quebra de linha, outro "@", ou passar do tamanho de um nome.
 */

export interface MencaoNoCursor {
  readonly ativo: boolean;
  /** O que foi digitado depois do "@", até o cursor. */
  readonly termo: string;
  /** Onde está o "@" no texto. */
  readonly inicio: number;
}

const INATIVO: MencaoNoCursor = { ativo: false, termo: '', inicio: -1 };
const TAMANHO_MAXIMO_DO_TERMO = 40;

/** Há um "@" sendo digitado na altura do cursor? */
export function lerMencao(texto: string, cursor: number): MencaoNoCursor {
  const antes = texto.slice(0, cursor);
  const arroba = antes.lastIndexOf('@');
  if (arroba < 0) return INATIVO;

  // O "@" começa uma palavra: no início, ou depois de espaço ou pontuação de
  // abertura. "email@dominio" não é menção.
  const anterior = antes[arroba - 1];
  if (anterior !== undefined && !/[\s(["']/.test(anterior)) return INATIVO;

  const termo = antes.slice(arroba + 1);
  if (termo.length > TAMANHO_MAXIMO_DO_TERMO || /[\n@]/.test(termo) || /^\s/.test(termo)) {
    return INATIVO;
  }
  return { ativo: true, termo, inicio: arroba };
}

/** Sem acento e em minúsculas: "joão" casa com "Joao". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * As pessoas cujo nome casa com o termo: qualquer palavra do nome começando
 * pelo que foi digitado ("so" acha "Maria Souza"), ou o nome inteiro.
 */
export function filtrarPessoas<T extends { readonly nome: string }>(
  pessoas: readonly T[],
  termo: string,
): readonly T[] {
  const alvo = normalizar(termo.trim());
  if (alvo === '') return pessoas;
  return pessoas.filter((pessoa) => {
    const nome = normalizar(pessoa.nome);
    return nome.startsWith(alvo) || nome.split(/\s+/).some((parte) => parte.startsWith(alvo));
  });
}

/** Troca o "@termo" pelo "@Nome " escolhido, e diz onde o cursor fica. */
export function inserirMencao(
  texto: string,
  mencao: MencaoNoCursor,
  cursor: number,
  nome: string,
): { readonly texto: string; readonly cursor: number } {
  const inserido = `@${nome} `;
  const novo = texto.slice(0, mencao.inicio) + inserido + texto.slice(cursor).replace(/^ /, '');
  return { texto: novo, cursor: mencao.inicio + inserido.length };
}

/**
 * Quem ainda está citado no texto na hora de enviar.
 *
 * A pessoa pode ter escolhido alguém e depois apagado o "@Nome": avisar quem
 * o texto não cita seria um chamado sem motivo aparente.
 */
export function mencoesNoTexto(
  texto: string,
  escolhidas: ReadonlyMap<string, string>,
): readonly string[] {
  return [...escolhidas].filter(([, nome]) => texto.includes(`@${nome}`)).map(([id]) => id);
}
